// The guide pages under src/views/content, grouped for the "Guides" section of
// the three dot menu.
//
// These pages carry real organic traffic but nothing on the site linked to
// them, so no internal link value passed between them. Listing them here gives
// every page a set of real anchors pointing at the cluster.
//
// Anchor text is deliberately the descriptive version rather than the raw page
// title, because the anchor is most of what an internal link is worth.
//
// Group order is by relevance to a typical visitor, not alphabetical or by
// when a group was added: AI and mental health first (the core topic most
// people arrive asking about), then the product itself, then decision-stage
// and everyday-support content, then the more specific segments last.

const GUIDE_GROUPS = [
  {
    heading: 'AI and mental health',
    links: [
      { to: '/is-ai-therapy-helpful', label: 'Is AI therapy actually helpful?' },
      { to: '/pros-and-cons-of-ai-therapy', label: 'The pros and cons of AI therapy' },
      { to: '/the-science-behind-ai-therapy', label: 'The science behind AI therapy' },
      { to: '/what-is-an-ai-therapist', label: 'What is an AI therapist?' },
      { to: '/using-ai-for-cbt', label: 'Using AI alongside CBT' },
      { to: '/using-ai-for-cbt-and-dbt', label: 'Using AI alongside CBT and DBT' },
      { to: '/overcoming-stigma-ai-mental-health', label: 'AI and the stigma around mental health' },
      { to: '/ai-mental-health', label: 'AI and mental health' },
      { to: '/ai-mental-health-companion', label: 'What a mental health companion does' },
    ],
  },
  {
    heading: 'Understanding ZOSA',
    links: [
      { to: '/understanding-zosa', label: 'How ZOSA works, and the science behind it' },
      { to: '/mind-graph', label: 'MindGraph, seeing your patterns' },
      { to: '/nd-info', label: 'ZOSA for neurodivergent minds' },
      { to: '/ai-support-for-adhd-and-rsd', label: 'AI support for ADHD and rejection sensitive dysphoria' },
      { to: '/zosa', label: 'About ZOSA' },
      { to: '/zosa-ai', label: 'The AI behind ZOSA' },
      { to: '/zosa-app', label: 'The ZOSA app' },
    ],
  },
  {
    heading: 'Comparing the options',
    links: [
      { to: '/best-ai-therapy-app', label: 'Choosing an AI therapy app' },
      { to: '/best-ai-therapist', label: 'Comparing AI therapists' },
      { to: '/best-ai-therapy', label: 'Comparing AI therapy tools' },
      { to: '/best-free-ai-therapy-bots', label: 'Free AI therapy bots compared' },
      { to: '/explore-free-ai-therapy-apps', label: 'Free AI therapy apps' },
      { to: '/top-ai-chatbots-for-mental-health', label: 'Top AI chatbots for mental health' },
      { to: '/which-ai-chatbot-is-best', label: 'Which AI chatbot suits you?' },
      { to: '/ai-therapy-apps', label: 'AI therapy apps, what to look for' },
      { to: '/ai-therapist', label: 'AI therapists, explained' },
      { to: '/ai-therapy', label: 'AI therapy, explained' },
      { to: '/virtual-therapist', label: 'Virtual therapists, explained' },
      { to: '/therapy-chatbot', label: 'Therapy chatbots, explained' },
      { to: '/zosa-vs-betterhelp-and-talkspace', label: 'ZOSA vs BetterHelp and Talkspace' },
    ],
  },
  {
    heading: 'Common questions',
    links: [
      { to: '/ai-companion-for-loneliness', label: 'Can an AI companion help with loneliness?' },
      { to: '/is-it-safe-to-talk-to-an-ai-about-your-mental-health', label: 'Is it safe to tell an AI about your mental health?' },
      { to: '/signs-of-burnout', label: 'Signs of burnout, and what actually helps' },
    ],
  },
  {
    heading: 'Working it out',
    links: [
      { to: '/is-my-problem-serious-enough', label: 'Is my problem serious enough to get help?' },
      { to: '/cant-afford-therapy', label: 'What to do when you cannot afford therapy' },
      { to: '/between-therapy-sessions', label: 'Support between therapy sessions' },
      { to: '/why-you-cant-see-your-own-patterns', label: 'Why you cannot see your own patterns' },
      { to: '/why-journalling-does-not-work', label: 'Why journalling does not work' },
      { to: '/how-to-track-your-mental-health', label: 'How to actually track your mental health' },
      { to: '/chatgpt-for-mental-health', label: 'Using ChatGPT for mental health' },
    ],
  },
  {
    heading: 'Support between sessions',
    links: [
      { to: '/mental-health-support', label: 'Finding mental health support' },
      { to: '/online-therapy', label: 'Online support, and where it helps' },
      { to: '/personalised-therapy', label: 'Support that adapts to you' },
      { to: '/anonymous-therapy', label: 'Talking anonymously' },
      { to: '/free-ai-therapy-chatbot', label: 'Free places to start' },
      { to: '/mental-well-being', label: 'Looking after your wellbeing' },
    ],
  },
  {
    heading: 'Mental fitness and performance',
    links: [
      { to: '/mental-fitness-tracker', label: 'Mental fitness tracking, the Strava for your mind' },
      { to: '/mental-health-competitive-edge', label: 'Improving your mental health to get an edge at work' },
      { to: '/mental-reps-building-resilience', label: 'Mental reps, building resilience like a muscle' },
      { to: '/track-your-mental-state-like-hrv', label: 'Why high performers track their mind like their HRV' },
      { to: '/mental-health-for-high-performers', label: 'Mental health for high performers and founders' },
    ],
  },
  {
    heading: 'Privacy and safety',
    links: [
      { to: '/zosecure', label: 'ZoSecure, how your data is protected' },
      { to: '/encrypted-therapy', label: 'Encrypted conversations' },
      { to: '/secure-mental-health-platform', label: 'What makes a platform secure' },
      { to: '/anonymous-ai-chatbot', label: 'Anonymous AI conversations' },
      { to: '/anonymous-ai-therapy', label: 'Anonymous support, explained' },
    ],
  },
  {
    heading: 'For employers',
    links: [
      { to: '/ai-benefits-for-employees', label: 'AI benefits for employees, what actually helps' },
      { to: '/eap-alternative', label: 'Why employers are adding AI support alongside an EAP' },
      { to: '/cost-of-employee-burnout', label: 'The cost of employee burnout' },
      { to: '/employee-mental-health-benefit-cost', label: 'What a mental health benefit should cost' },
      { to: '/how-to-choose-employee-mental-health-benefit', label: 'Choosing a benefit your team will use' },
    ],
  },
];

export default GUIDE_GROUPS;
